import { INSTAGRAM_CONTENT_TYPES, SOCIAL_PLATFORMS } from '~/constants';
import { Thumbnails } from '~/screens/ContentSharedScreen/ContentSharedScreen.types';
import {
  ContentAssetApiResponse,
  ContentsApiResponse,
  INSTAGRAM_PRODUCT_TYPES,
  InstagramContentApiResponse,
  SearchApiRequestParams,
  YoutubeContentApiResponse,
} from '~/searchApi/content';

import { ORDER } from '../searchApi.constants';
import {
  ContentApiCreator,
  TiktokContentApiResponse,
} from './searchContentApi.api-model';
import {
  ASSET_TYPE,
  IApiSearchInstagramContent,
  IApiSearchTiktokContent,
  IApiSearchYoutubeContent,
  ISearchContentCreator,
  ISearchContentParams,
  ISearchContentResult,
  SearchContentVmResponse,
} from './searchContentApi.model';

export const mapSearchContentParamsFromVmToApi = ({
  filters,
  managerId,
  orderBy,
  order,
  page = 1,
  pageSize,
  platform,
  searchQuery,
  productType,
  searchQueryFuzzyMatch,
}: Partial<ISearchContentParams>): SearchApiRequestParams => ({
  filters: filters,
  manager_id: managerId,
  order_by: orderBy,
  order: order ?? ORDER.DESC,
  page: page,
  platforms: platform,
  product_type: productType,
  q: searchQuery && searchQuery.length ? searchQuery : undefined,
  size: pageSize,
  q_fuzzy_match: searchQueryFuzzyMatch,
});

export const mapSearchContentFromApiToVm = (
  searchContentApiResponse: ContentsApiResponse,
): SearchContentVmResponse => ({
  data: mapApiSearchContentResultsFromApiToVm(searchContentApiResponse.results),
  meta: searchContentApiResponse.meta,
});

export const mapApiSearchContentResultsFromApiToVm = (
  contents: ContentsApiResponse['results'],
): ISearchContentResult[] =>
  contents.map((content) => {
    if (content.platform === SOCIAL_PLATFORMS.youtube) {
      return mapApiSearchYoutubeContentFromApiToVm(content);
    }
    if (content.platform === SOCIAL_PLATFORMS.tiktok) {
      return mapApiSearchTiktokContentFromApiToVm(content);
    }

    return mapApiSearchInstagramContentFromApiToVm(content);
  });

export const mapInstagramProductTypeApiToVm = (
  productType: InstagramContentApiResponse['product_type'],
): INSTAGRAM_CONTENT_TYPES => {
  return {
    [INSTAGRAM_PRODUCT_TYPES.FEED]: INSTAGRAM_CONTENT_TYPES.post,
    [INSTAGRAM_PRODUCT_TYPES.STORY]: INSTAGRAM_CONTENT_TYPES.story,
    [INSTAGRAM_PRODUCT_TYPES.REELS]: INSTAGRAM_CONTENT_TYPES.reels,
  }[productType];
};

export const mapInstagramAssetsFromApiToVm = (
  assets: ContentAssetApiResponse[],
) =>
  assets.map((asset) => ({
    height: asset.height,
    previewUrl: asset.thumbnail,
    type: asset.type === 'IMAGE' ? ASSET_TYPE.image : ASSET_TYPE.video,
    url: asset.url,
    width: asset.width,
  }));

export const mapTiktokAssetsFromApiToVm = (assets: ContentAssetApiResponse[]) =>
  assets.map((asset) => ({
    previewUrl: asset.thumbnail,
    url: asset.thumbnail,
    height: asset.height,
    width: asset.width,
  }));

export const mapYouTubeAssetsFromApiToVm = (
  assets: ContentAssetApiResponse[],
) =>
  assets.map((asset) => ({
    name: asset.name,
    url: asset.url,
    width: asset.width,
    height: asset.height,
  }));

export const mapYouTubeAssetsFromApiToThumbnails = (
  assets: ContentAssetApiResponse[],
) =>
  assets.reduce((obj, item) => {
    return {
      ...obj,
      [item.name as string]: item,
    };
  }, {}) as Thumbnails;

export const mapApiSearchCreatorFromApiToVm = (
  creator: ContentApiCreator | null,
): ISearchContentCreator => {
  /**
   * - Returns default values for required fields when creator is null
   * - Prevents runtime errors in pagination when content has no creator
   */
  if (!creator) {
    return {
      id: '',
      status: 'not-connected',
      platforms: {},
    };
  }

  const {
    avatar,
    first_name,
    full_name,
    id,
    last_name,
    name,
    slug,
    status,
    platforms,
  } = creator;

  return {
    avatar,
    firstName: first_name,
    lastName: last_name,
    fullName: full_name,
    id,
    name,
    slug,
    status,
    // @ts-ignore Inconsistent types :\
    platforms,
  };
};

export const mapApiSearchInstagramContentFromApiToVm = ({
  assets,
  comment_count: comments,
  content_type: type,
  created_at: createdTime,
  creator,
  description: caption,
  engagement_count: engagements,
  id: contentId,
  impression_count: impressions,
  interaction_count: interactions,
  like_count: likes,
  play_count: plays,
  post_followers_eng_rate: postFollowersEngRate,
  post_reach_eng_rate: postReachEngRate,
  product_type,
  published_at: publishedAt,
  reach_count: reach,
  saved_count: saved,
  share_count: shares,
  share_link: shareLink,
  share_url: shareUrl,
  status: status,
  updated_at: updatedAt,
  insight_last_updated_at: insightLastUpdatedAt,
  video_view_count: views,
  views_eng_rate: viewsEngRate,
  ig_reels_aggregated_all_plays_count: igReelsAggregatedAllPlaysCount,
  reply_count: replies,
  is_collaboration: isCollaboration,
  views: viewsCount,
  id,
}: InstagramContentApiResponse): IApiSearchInstagramContent => ({
  assets: mapInstagramAssetsFromApiToVm(assets),
  caption,
  channel: mapInstagramProductTypeApiToVm(product_type),
  comments,
  contentId,
  createdTime,
  creator: mapApiSearchCreatorFromApiToVm(creator),
  engagements,
  impressions,
  interactions,
  likes,
  platform: SOCIAL_PLATFORMS.instagram,
  plays,
  postFollowersEngRate,
  postReachEngRate,
  publishedAt,
  reach,
  saved,
  status,
  shareLink,
  shares,
  shareUrl,
  type: type === 'VIDEO' ? ASSET_TYPE.video : ASSET_TYPE.image,
  updatedAt,
  insightLastUpdatedAt,
  views,
  viewsEngRate,
  igReelsAggregatedAllPlaysCount: igReelsAggregatedAllPlaysCount ?? plays,
  postedAt: publishedAt,
  replies,
  isCollaboration,
  id,
  viewsCount,
  productType: product_type,
  contentType: type,
});

export const mapApiSearchTiktokContentFromApiToVm = ({
  assets,
  comment_count: comments,
  created_at: createdTime,
  creator,
  description: caption,
  embed_url: embedUrl,
  engagement_count: engagements,
  engagement_rate: engagementRate,
  external_url: externalUrl,
  follower_engagement_rate: followerEngagementRate,
  id: contentId,
  like_count: likes,
  published_at: publishedAt,
  share_count: shares,
  share_link: shareLink,
  updated_at: updatedAt,
  insight_last_updated_at: insightLastUpdatedAt,
  view_count: views,
  id,
}: TiktokContentApiResponse): IApiSearchTiktokContent => ({
  assets: mapTiktokAssetsFromApiToVm(assets),
  caption,
  comments,
  contentId,
  createdTime,
  creator: mapApiSearchCreatorFromApiToVm(creator),
  embedUrl,
  engagementRate,
  engagements,
  externalUrl,
  followerEngagementRate,
  likes,
  platform: SOCIAL_PLATFORMS.tiktok,
  publishedAt,
  shareLink,
  shares,
  type: ASSET_TYPE.video,
  updatedAt,
  insightLastUpdatedAt,
  views,
  createTime: publishedAt,
  engagementCount: engagements,
  videoViews: views,
  id,
});

export const mapApiSearchYoutubeContentFromApiToVm = ({
  assets,
  comment_count: comments,
  created_at: createdTime,
  creator,
  description: caption,
  dislike_count: dislikes,
  engagement_rate: engagements,
  favorite_count: favourites,
  id: contentId,
  like_count: likes,
  published_at: publishedAt,
  network_id: networkId,
  share_count: shares,
  share_link: shareLink,
  updated_at: updatedAt,
  insight_last_updated_at: insightLastUpdatedAt,
  view_count: views,
  view_rate: viewRate,
  id,
}: YoutubeContentApiResponse): IApiSearchYoutubeContent => ({
  assets: mapYouTubeAssetsFromApiToVm(assets),
  thumbnails: mapYouTubeAssetsFromApiToThumbnails(assets),
  caption,
  comments,
  contentId,
  createdTime,
  creator: mapApiSearchCreatorFromApiToVm(creator),
  dislikes,
  engagements,
  favourites,
  likes,
  networkId,
  platform: SOCIAL_PLATFORMS.youtube,
  publishedAt,
  shareLink,
  shares,
  type: ASSET_TYPE.video,
  updatedAt,
  insightLastUpdatedAt,
  views,
  ytVideoId: networkId,
  viewCount: views,
  likeCount: likes,
  commentCount: comments,
  dislikeCount: dislikes,
  viewRate,
  id,
});
